import type React from "react"
import { useMemo, useState } from "react"
import { Calendar, Loader } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent } from "../../components/ui/card"
import { Checkbox } from "../../components/ui/checkbox"
import { Input } from "../../components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover"
import { Calendar as CalendarComponent } from "../../components/ui/calendar"
import { cn } from "../../lib/utils"
import useSWR from "swr"
import { fetcher, handleMutation } from "../../util/axios"
import placeholder from "../../util/placeholder"
import dayjs from "dayjs"
import useSWRMutation from "swr/mutation";
import requestErrorHandler from '../../util/requestErrorHandler';
import { message } from "antd"
import { PlacesAutocomplete } from "../../components/ui/PlaceAutoComplete"
import { Label } from "../../components/ui/label"
// import { useCurrency } from "../../hooks/useCurrency"

// type Coordinates = {
//   lat: number;
//   lng: number;
//   address: string;
// }

export default function CreateTransaction() {
  const [date, setDate] = useState<Date | undefined>()
  const [selectedVehicles, setSelectedVehicles] = useState<{
    vehicleId: string,
    numberOfTrips: number,
  }[]>([])
  const [selectedTruck, setSelectedTruck] = useState<TruckCategory>();
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingPoint, setLoadingPoint] = useState("");
  const [dumpingPoint, setDumpingPoint] = useState("");
  const [renterName, setRenterName] = useState("");
  const [renterPhone, setRenterPhone] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [totalAmount, setTotalAmount] = useState<number | undefined>();
  // const [formatCurrency] = useCurrency();

  const { data, } = useSWR<{ truckCategories: TruckCategory[] }>(
    { url: "newAdmin/truck-category-all" },
    fetcher,
  );

  const { trigger, isMutating } = useSWRMutation(
    "fleet/rental-records",
    handleMutation
  );

  const truckCategories = data?.truckCategories ?? []

  const { data: trucksData, isLoading: isLoadingVehicles } = useSWR({ url: "fleet/vehicles" }, fetcher);

  const filteredVehicles: Vehicle[] = useMemo(() => {
    return trucksData?.data?.vehicles.filter((vehicle: any) => vehicle.regPlate.toLowerCase().includes(searchQuery.toLowerCase())) || []
  }, [trucksData, searchQuery]);

  // Handle input changes
  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => setter(e.target.value);
  };

  const toggleVehicleSelection = (id: string) => {
    const found =   selectedVehicles.find(v => v.vehicleId === id);
    if (found) {
      setSelectedVehicles(selectedVehicles.filter((v) => v.vehicleId !== id))
    } else {
      setSelectedVehicles([
        ...selectedVehicles,
        {
          vehicleId: id,
          numberOfTrips: 0
        }
      ])
    }
  }

  const includesVehicleId = (id: string) => {
    const found = selectedVehicles.find(v => v.vehicleId === id);
    return !!found;
  }

  const setVehicleTrips = (value: number, id:string) => {
    const found = selectedVehicles.find(v => v.vehicleId === id);
    if (found) {
      setSelectedVehicles(selectedVehicles.map((v) => {
        if (v.vehicleId === id) {
          return {...found, numberOfTrips: value}
        }
        return {...v}
      }))
    } else {
      setSelectedVehicles([
        ...selectedVehicles,
        {
          vehicleId: id,
          numberOfTrips: value
        }
      ])
    }
  }

  const showRightColumn = useMemo(() => {
    return [
      loadingPoint,
      dumpingPoint,
      renterName,
      renterPhone,
      selectedMaterial,
      totalAmount,
      date,
    ].every(field => field && (field !== ""));
  }, [
    loadingPoint,
    dumpingPoint,
    renterName,
    renterPhone,
    selectedMaterial,
    date,
    totalAmount
  ]);

  const createRecord = async () => {
    try {
      const res = await trigger({
        rentalDateTime: date,
        truckCategory: selectedTruck?.id,
        material: selectedMaterial,
        loadingPoint,
        dumpingPoint,
        renterName,
        renterPhone,
        note: "",
        vehicleSelections: selectedVehicles.map((vehicle) => ({
          vehicleId: vehicle.vehicleId,
          numberOfTrips: vehicle.numberOfTrips,
          pricePerTrip: totalAmount ? (totalAmount / vehicle.numberOfTrips) : 0
        }))
      });
      message.success(res.message);
      setSelectedVehicles([]);
    } catch (error) {
      requestErrorHandler(error);
    }
  }

  return (
    <div className="h-full flex bg-white rounded-lg">
      {/* Main Content */}
      <div className="flex-1 overflow-auto">
    
        {/* Content */}
        <div className="p-6 w-full ">
          <h1 className="text-2xl font-semibold mb-8">Create Rental Record</h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left Column */}
            <div className="space-y-8">
              {/* Category Selection */}
              <div className="mb-8">
                <Label className="text-base font-medium mb-3 block text-gray-500">Select Category and material</Label>

                <div className="grid grid-cols-3 md:grid-cols-4 gap-4 md:max-w-3xl">
                  {truckCategories?.map((type) => (
                    <div
                      key={type.id}
                      className={`border  rounded-md p-4 flex flex-col items-center cursor-pointer transition-all ${selectedTruck?.id === type.id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                        }`}
                      onClick={() => setSelectedTruck(type)}
                    >
                      <div className="size-20 mb-2 overflow-hidden relative">
                        <img src={!type.image || type.image.includes("example") ? placeholder : type.image} alt={type.name} className="object-contain" />
                      </div>
                      <span className="text-sm text-center">{type.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {selectedTruck && (<>
                  {/* Material Selection */}
                  <div className="mb-6">
                    <Label className="text-base font-medium mb-2 block text-gray-500">Select Material</Label>
                    <div className="flex gap-2">
                      {
                        selectedTruck?.materials?.map((material, index) => (
                          <div
                              key={index}
                              style={{ border: selectedMaterial === material.name ? `5px solid #0063cf` : "5px solid transparent" }}
                              onClick={() => {
                                setSelectedMaterial(material.name)
                              }}
                              className={`relative  border cursor-pointer active:opacity-50 h-10 overflow-hidden flex justify-center items-center`}
                            >
                            <img
                              src={material.image || placeholder}
                              alt={`${selectedTruck.name} ${material.name}`}
                              width={100}
                            />
                            <div className="bg-[#00000055] absolute w-full h-full flex justify-center items-center">
                              <h3 className="capitalize font-bold text-white">
                                {material.name}
                              </h3>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>              
                {/* Trip Time and Locations */}
                <div>
                  <h2 className="text-gray-500 mb-4">Provide trip time and locations</h2>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal bg-blue-500 text-white hover:bg-blue-600 hover:text-white",
                          !date && "text-muted-foreground",
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4 text-white" />
                        {date ? dayjs(date).format("DD MMM YYYY") : <span className="text-white">Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent mode="single" selected={date} onSelect={setDate} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Loading Point */}
                <div>
                  {/* Loading Point */}
                  <div className="mb-4">
                    <Label className="text-gray-500 mb-2 flex items-center gap-2">
                      Loading Point
                    </Label>
                    <PlacesAutocomplete
                      placeholder="Search for loading location"
                      value={loadingPoint}
                      onChange={(_, details) => setLoadingPoint(details?.address || '')}
                      // zoneLabel={pickupLabel}
                    />
                  </div>

                  {/* Dumping Point */}
                  <div className="mb-4">
                    <h2 className="text-gray-500 mb-2 flex items-center gap-2">
                      Dumping Point
                    </h2>
                    <PlacesAutocomplete
                      placeholder="Search for dumping location"
                      value={dumpingPoint}
                      onChange={(_, details) => setDumpingPoint(details?.address || '')}
                      // zoneLabel={dumpingLabel}
                    />
                  </div>

                  {/* Renter Name */}
                  <div className="mb-4">
                    <h2 className="text-gray-500 mb-2">Renter Name</h2>
                    <input
                      type="text"
                      className="ring-1 ring-gray-300 p-2 rounded-sm w-full"
                      placeholder="Renter name"
                      value={renterName}
                      onChange={handleInputChange(setRenterName)}
                    />
                  </div>

                  {/* Renter Phone */}
                  <div className="mb-4">
                    <h2 className="text-gray-500 mb-2">Renter Phone</h2>
                    <input
                      type="text"
                      className="ring-1 ring-gray-300 p-2 rounded-sm w-full"
                      placeholder="Renter phone"
                      value={renterPhone}
                      onChange={handleInputChange(setRenterPhone)}
                    />
                  </div>
                  {/* Rental Value*/}
                  <div className="mb-4">
                    <h2 className="text-gray-500 mb-2">Rental Value</h2>
                    <input
                      type="number"
                      className="ring-1 ring-gray-300 p-2 rounded-sm w-full"
                      placeholder="Renter Value"
                      value={totalAmount}
                      onChange={(e) => setTotalAmount(Number(e.target.value))}
                    />
                  </div>
                </div>
              </>)}

              {/* Price per Trip */}
             {/* {showRightColumn && <Card className="border border-dashed border-blue-200 bg-blue-50">
                <CardContent className="px-4 flex justify-between items-center">
                  <div className="text-blue-600 text-2xl font-semibold">
                    {formatCurrency(selectedTruck?.pricePerTrip || 0)}
                  </div>
                  <div className="text-blue-500">Price per Trip</div>
                </CardContent>
              </Card>} */}
            </div>

            {/* Right Column */}
            { showRightColumn && <div>
              <Label className="text-base font-medium mb-3 block text-gray-500">Select vehicle and enter trips</Label>
              <Input
                placeholder="Search"
                className="mb-6"
                onChange={handleInputChange(setSearchQuery)}
              />

              <div className="space-y-3">
                {filteredVehicles.map((vehicle) => (
                  <Card
                    key={vehicle.id}
                    className={cn(
                      "border p-4 rounded-none",
                      includesVehicleId(vehicle.id) ? "border-blue-200" : "border-gray-200",
                    )}
                  >
                    <CardContent className="px-4 py-0 flex items-center">
                      <Checkbox
                        checked={includesVehicleId(vehicle.id)}
                        onCheckedChange={() => toggleVehicleSelection(vehicle.id)}
                        className="mr-4"
                      />
                      <div className="flex items-center gap-3">
                        <img src={vehicle.truckCategoryDetails.image || placeholder} alt={vehicle.truckCategoryDetails.description} className="w-10 h-10" />
                        <div>
                          <div className="font-medium">{vehicle?.regPlate}</div>
                          <div className="text-xs text-gray-500">
                            Last Trip <span className="text-blue-500">{dayjs().format('DD/MM/YYYY')}</span>
                          </div>
                        </div>
                      </div>
                      {includesVehicleId(vehicle.id) && (
                        <Input
                          className="ml-auto  bg-blue-100 border-none text-blue-600 w-12 h-8 rounded-md flex items-center justify-center"
                          onChange={(e) => setVehicleTrips(Number(e.target.value), vehicle.id)}
                        />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Total */}
              {/* {selectedVehicles.length > 0 && <Card className="mt-8 border border-dashed border-blue-200 bg-blue-50">
                <CardContent className="p-4 flex justify-between items-center">
                  <div className="text-gray-500">Total</div>
                  <div className="text-blue-600 text-2xl font-semibold">1,080,000</div>
                </CardContent>
              </Card>} */}

              {/* Action Buttons */}
              <div className="mt-8 space-y-4">
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={!selectedVehicles.length || isMutating}
                  onClick={createRecord}
                  
                >
                  {isMutating ? <Loader /> : 'Place Rental'}
                </Button>
                <Button variant="ghost" className="w-full text-gray-500">
                  Cancel
                </Button>
              </div>
            </div>}
          </div>
        </div>
      </div>
    </div>
  )
}
